import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Alert, Box, Button, Card, CardContent, CardHeader, Container, Grid, LinearProgress, Link, Stack, Typography } from "@mui/material";
import BreadcrumbBuilder from "../Common/Breadcrumbs/BreadcrumbBuilder";
import PasswordDialog from "./PasswordDialog";
import ProfileEditDialog from "./ProfileEditDialog";
import SecuritySettingsDialog from "./SecuritySettingsDialog";
import RenderDate from "../Common/Date/RenderDate";

import VpnKeyIcon from "@mui/icons-material/VpnKey";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SecurityIcon from "@mui/icons-material/Security";

import { getProfile } from "../../reducers";
import accountActions from "../../actions/accountActions";

import { Ethnicities } from "../../constants";
import { displayFullName } from "../../utilities/formatters";

const Profile = () => {
    const dispatch = useDispatch();
    const profile = useSelector(getProfile);

    const [openDialog, setOpenDialog] = useState(null);

    useEffect(() => {
        dispatch(accountActions.getProfile());
    }, [dispatch]);

    if (!profile) return <LinearProgress />;

    const account = profile?.accounts?.length > 0 ? profile.accounts[0] : { name: "n/a" };
    const dob = !!profile?.dob ? <RenderDate date={profile.dob} /> : "n/a";
    const ethnicity = Ethnicities.find(({code}) => code === profile?.ethnicity);

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>
                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            title={
                                <Typography component="h2" className="card-header-title" >
                                    Your details
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle" >
                                    {profile?.username || ""}
                                </Typography>
                            }
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    <Button
                                        variant="contained"
                                        color="neutral"
                                        disableElevation={true}
                                        aria-label="edit profile"
                                        onClick={() => setOpenDialog("edit")}
                                        startIcon={<EditOutlinedIcon />}
                                    >
                                        Edit
                                    </Button>
                                </Stack>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <Grid container spacing={4}>
                                    <Grid item md={12} xl={6}>
                                        <dl className="list-data">
                                            <dt>Name</dt>
                                            <dd>{displayFullName(profile)}</dd>
                                            <dt>Job role</dt>
                                            <dd>{profile?.jobRole || "n/a"}</dd>
                                            <dt>Area of work</dt>
                                            <dd>{profile?.areaOfWork || "n/a"}</dd>
                                            <dt>Email</dt>
                                            <dd>{profile?.email || "n/a"}</dd>
                                        </dl>
                                    </Grid>
                                    <Grid item md={12} xl={6}>
                                        <dl className="list-data">
                                            <dt>Date of birth</dt>
                                            <dd>{dob}</dd>
                                            <dt>Gender</dt>
                                            <dd>{profile?.gender || "n/a"}</dd>
                                            <dt>Ethnicity</dt>
                                            <dd>{!!ethnicity ? `${ethnicity.code} - ${ethnicity.description}` : "n/a"}</dd>
                                        </dl>
                                    </Grid>
                                    <Grid item md={12} xl={6}>
                                        <dl className="list-data">
                                            <dt>Organisation</dt>
                                            <dd>{profile?.organisation?.name || "n/a"}</dd>
                                            <dt>Account</dt>
                                            <dd>{account.name}</dd>
                                            <dt>UUID</dt>
                                            <dd>{profile?.uuid || "n/a"}</dd>
                                            <dt>External ID</dt>
                                            <dd>{profile?.external?.reference || ""}</dd>
                                        </dl>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>

                        <CardContent>
                            <Box className="card-content">
                                <Alert
                                    severity="info"
                                    icon={<InfoOutlinedIcon fontSize="medium" />}
                                >
                                    You can update your profile avatar with a free &nbsp;
                                    <Link
                                        href="https://en.gravatar.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Gravatar
                                    </Link>{" "}
                                    account.
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            title={
                                <Typography component="h2" className="card-header-title" >
                                    Security
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle" >
                                    Security settings
                                </Typography>
                            }
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    <Button
                                        variant="contained"
                                        color="neutral"
                                        disableElevation={true}
                                        aria-label="change password"
                                        onClick={() => setOpenDialog("password")}
                                        startIcon={<VpnKeyIcon />}
                                    >
                                        Change password
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="neutral"
                                        disableElevation={true}
                                        aria-label="security settings"
                                        onClick={() => setOpenDialog("settings")}
                                        startIcon={<SecurityIcon />}
                                    >
                                        MFA settings
                                    </Button>
                                </Stack>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <Grid container spacing={4}>
                                    <Grid item md={12} xl={6}>
                                        <dl className="list-data">
                                            <dt>MFA enabled</dt>
                                            <dd>{!!profile?.security?.MFA?.isEnabled ? "yes" : "no"}</dd>
                                        </dl>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            {!!profile && openDialog === "edit" && (
                <ProfileEditDialog
                    open
                    profile={profile}
                    onClose={() => setOpenDialog(null)}
                />
            )}
            {openDialog === "password" && (
                <PasswordDialog open onClose={() => setOpenDialog(null)} />
            )}
            {openDialog === "settings" && (
                <SecuritySettingsDialog
                    open
                    data={profile?.security}
                    onClose={() => setOpenDialog(null)}
                />
            )}
        </Container>
    );
};

export default Profile;
